import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SquareStack, Plus, Search, X, Check, RotateCcw, Trash2, Loader2, Languages, Type, Quote, Tags, ChevronDown, CalendarDays, Info } from 'lucide-react';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import { emitLeitnerChanged, leitnerService, LeitnerCardDto, LeitnerCardWriteDto, LeitnerSummaryDto } from '../../services/leitnerService';
import { WordPartOfSpeech } from '../../services/contentService';
import { WORD_POS_I18N, WORD_POS_OPTIONS } from '../../utils/wordPartOfSpeech';
import { FarsiKeyboardInput } from '../shared/FarsiKeyboardInput';
import { ProfileComboList } from '../shared/ProfileComboList';
import { ModalTourHelpButton } from '../tour/ModalTourHelpButton';
import { emitLearnerTourOverlayPageKey } from '../../contexts/ProductTourContext';
import { TOUR_TARGET_FOCUS_EVENT, type TourTargetEventDetail } from '../../learnerTour/tourTargetEvents';
import { LEARNER_UI_PERSIAN_STACK } from '../../constants/learnerPersianFonts';
import { learnerSidePanelMotionClass, learnerSidePanelPlacement } from '../../utils/learnerSidePanel';
import { LEARNER_SIDE_BACKDROP_MS } from '../../utils/learnerUiAnim';
import { useModalBackdropFade } from '../../utils/learnerSlideModalMotion';
import { useNotifications } from '../feedback/NotificationSystem';
import { LEARNER_SETTINGS_MODAL_CHROME_CLASS } from '../settings/learnerSettingsSectionThemes';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import { VocabCollapseMedia, VocabWordImage } from '../shared/VocabCollapseMedia';
import { VocabPronounceButton } from '../shared/VocabPronounceButton';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';
import { MmePickBack, MmePickList } from '../dashboard/MmePickList';
import {
  LeitnerModalTabs,
  leitnerTabCarouselIndex,
  leitnerTabCarouselOrder,
  LEITNER_TAB_ORDER,
  type LeitnerTabId
} from './LeitnerModalTabs';

type Tab = LeitnerTabId;

export const LEITNER_OPEN_EVENT = 'app:openLeitner';

interface LeitnerBoxPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Same corner anchor as the AI assistant panel. */
  anchorStyle?: React.CSSProperties;
  initialCardId?: number;
  initialQuery?: string;
}

const emptyForm = (): LeitnerCardWriteDto => ({
  persian: '',
  english: '',
  partOfSpeech: WordPartOfSpeech.Other
});

/** Matches server BoxIntervalDays indices 1–5. */
const BOX_INTERVAL_DAYS = [1, 3, 7, 14, 30] as const;

const WORD_PAGE_SIZE = 10;

/** Box 1 is the lightest blue; box 5 is the darkest. */
const BOX_TONES = [
  'bg-sky-100 text-sky-900 hover:bg-sky-200 dark:bg-sky-200 dark:text-sky-950 dark:hover:bg-sky-300',
  'bg-sky-200 text-sky-950 hover:bg-sky-300 dark:bg-sky-300 dark:text-sky-950 dark:hover:bg-sky-400',
  'bg-sky-300 text-sky-950 hover:bg-sky-400 dark:bg-sky-400 dark:text-sky-950 dark:hover:bg-sky-500',
  'bg-sky-500 text-white hover:bg-sky-600 dark:bg-sky-600 dark:text-white dark:hover:bg-sky-700',
  'bg-sky-700 text-white hover:bg-sky-800 dark:bg-sky-800 dark:text-white dark:hover:bg-sky-900'
] as const;

const ADD_FIELD_TONES = [
  'border border-rose-200/80 bg-rose-50/80 dark:border-rose-900/60 dark:bg-rose-950/30',
  'border border-sky-200/80 bg-sky-50/80 dark:border-sky-900/60 dark:bg-sky-950/30',
  'border border-violet-200/80 bg-violet-50/80 dark:border-violet-900/60 dark:bg-violet-950/30',
  'border border-amber-200/80 bg-amber-50/80 dark:border-amber-900/60 dark:bg-amber-950/30',
  'border border-emerald-200/80 bg-emerald-50/80 dark:border-emerald-900/60 dark:bg-emerald-950/30'
] as const;

function cardToWrite(card: LeitnerCardDto): LeitnerCardWriteDto {
  return {
    persian: card.persian,
    english: card.english,
    phonetic: card.phonetic,
    partOfSpeech: card.partOfSpeech,
    example: card.example,
    imageUrl: card.imageUrl,
    audioUrl: card.audioUrl,
    audioFileName: card.audioFileName,
    sourceKind: card.sourceKind,
    sourceLessonId: card.sourceLessonId,
    sourceLibraryBookId: card.sourceLibraryBookId
  };
}

export const LeitnerBoxPanel: React.FC<LeitnerBoxPanelProps> = ({
  isOpen,
  onClose,
  anchorStyle,
  initialCardId,
  initialQuery
}) => {
  const { t } = useTranslation('leitner');
  const { showSuccess, showError } = useNotifications();
  const { locale, direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  useHistoryBackClose(isOpen, onClose, 'leitner');

  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const [leitnerMenu, setLeitnerMenu] = useState(() => !initialCardId);
  const [tab, setTab] = useState<Tab>('due');
  const [tabDragOffsetPx, setTabDragOffsetPx] = useState(0);
  const [tabDragging, setTabDragging] = useState(false);
  const [tabMotionReady, setTabMotionReady] = useState(false);
  const tabRef = useRef(tab);
  const tabDragOffsetPxRef = useRef(0);
  const isRtlRef = useRef(isRtl);
  const cardsScrollRef = useRef<HTMLDivElement>(null);
  const [summary, setSummary] = useState<LeitnerSummaryDto | null>(null);
  const [due, setDue] = useState<LeitnerCardDto[]>([]);
  const [dueIndex, setDueIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [words, setWords] = useState<LeitnerCardDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [wordsLoading, setWordsLoading] = useState(false);
  const [wordsLoadingMore, setWordsLoadingMore] = useState(false);
  const wordsRequestRef = useRef(0);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState<LeitnerCardWriteDto>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [removePendingId, setRemovePendingId] = useState<number | null>(null);
  const [removeClosingId, setRemoveClosingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [expandedWordId, setExpandedWordId] = useState<number | null>(null);
  const [openBoxGuide, setOpenBoxGuide] = useState<number | null>(null);
  const confirmAnimMs = 220;

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await leitnerService.getSummary());
    } catch {
      /* ignore */
    }
  }, []);

  const loadDue = useCallback(async () => {
    const res = await leitnerService.list({ dueOnly: true, page: 1, pageSize: 50 });
    setDue(res.items);
    setDueIndex(0);
    setRevealed(false);
  }, []);

  const loadWords = useCallback(async (p: number, q: string, append = false) => {
    const req = ++wordsRequestRef.current;
    if (append) setWordsLoadingMore(true);
    else {
      setWordsLoading(true);
      setWordsLoadingMore(false);
    }
    try {
      const res = await leitnerService.list({ q, page: p, pageSize: WORD_PAGE_SIZE });
      if (req !== wordsRequestRef.current) return;
      setWords((prev) => {
        if (!append) return res.items;
        const seen = new Set(prev.map((card) => card.id));
        return [...prev, ...res.items.filter((card) => !seen.has(card.id))];
      });
      setTotal(res.total);
      setPage(p);
    } finally {
      if (req !== wordsRequestRef.current) return;
      if (append) setWordsLoadingMore(false);
      else setWordsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setLeitnerMenu(!initialCardId);
    void loadSummary();
    void loadDue();
    const q = initialQuery?.trim() ?? '';
    setQuery(q);
    void loadWords(1, q).then(() => {
      if (!initialCardId) {
        setHighlightId(null);
        setExpandedWordId(null);
        return;
      }
      setTab('words');
      setHighlightId(initialCardId);
      setExpandedWordId(initialCardId);
      void leitnerService.get(initialCardId).then((card) => {
        setWords((prev) => (prev.some((c) => c.id === card.id) ? prev : [card, ...prev]));
      }).catch(() => undefined);
    });
  }, [isOpen, initialCardId, initialQuery, loadSummary, loadDue, loadWords]);

  useEffect(() => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (isOpen) {
      setShouldRender(true);
      setIsAnimatingOpen(false);
      openTimerRef.current = window.setTimeout(() => {
        setIsAnimatingOpen(true);
      }, 30);
      return;
    }

    setIsAnimatingOpen(false);
    emitLeitnerChanged();
    closeTimerRef.current = window.setTimeout(() => {
      setShouldRender(false);
    }, LEARNER_SIDE_BACKDROP_MS + 40);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      emitLearnerTourOverlayPageKey('leitner', true);
      return () => emitLearnerTourOverlayPageKey(null, false);
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onFocus = (event: Event) => {
      const key = (event as CustomEvent<TourTargetEventDetail>).detail?.targetKey;
      if (!key) return;
      const nextTab: Tab | null =
        key === 'leitner-tab-due' || key === 'leitner-due-card'
          ? 'due'
          : key === 'leitner-tab-words' || key === 'leitner-word-list'
            ? 'words'
            : key === 'leitner-tab-add' || key === 'leitner-add-form'
              ? 'add'
              : null;
      if (!nextTab) return;
      const tabOnly = key === 'leitner-tab-due' || key === 'leitner-tab-words' || key === 'leitner-tab-add';
      flushSync(() => {
        setLeitnerMenu(tabOnly);
        setTab(nextTab);
      });
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
  }, [isOpen]);

  useEffect(() => {
    isRtlRef.current = isRtl;
  }, [isRtl]);

  useLayoutEffect(() => {
    tabRef.current = tab;
  }, [tab]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setTabMotionReady(false);
      setTabDragOffsetPx(0);
      setTabDragging(false);
      tabDragOffsetPxRef.current = 0;
      return undefined;
    }
    const id = requestAnimationFrame(() => setTabMotionReady(true));
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  /** Touch carousel between Leitner tabs — same RTL-aware swipe as quick settings. */
  useEffect(() => {
    if (!shouldRender) return undefined;
    const el = cardsScrollRef.current;
    if (!el) return undefined;

    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let dragging = false;
    let ignoreGesture = false;
    let axis: 'horizontal' | 'vertical' | null = null;
    const SWIPE_DISTANCE_PX = 50;
    const SWIPE_VELOCITY_PX_PER_MS = 0.4;
    let startTime = 0;

    const onTouchStart = (e: TouchEvent) => {
      const target = e.target;
      if (target instanceof Element && target.closest('[data-no-tab-swipe]')) {
        ignoreGesture = true;
        return;
      }
      ignoreGesture = false;
      const touch = e.touches[0];
      if (!touch || e.touches.length !== 1) return;
      startX = touch.clientX;
      startY = touch.clientY;
      lastX = touch.clientX;
      startTime = Date.now();
      dragging = false;
      axis = null;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (ignoreGesture) return;
      const touch = e.touches[0];
      if (!touch || e.touches.length !== 1) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      lastX = touch.clientX;

      if (axis === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        }
      }
      if (axis === 'vertical') {
        if (dragging) {
          dragging = false;
          setTabDragging(false);
          tabDragOffsetPxRef.current = 0;
          setTabDragOffsetPx(0);
        }
        return;
      }
      if (axis !== 'horizontal') return;

      if (!dragging) {
        dragging = true;
        setTabDragging(true);
      }
      const idx = LEITNER_TAB_ORDER.indexOf(tabRef.current);
      const width = el.clientWidth || 1;
      const rtl = isRtlRef.current;
      let offset = dx;
      const atFirstRubber = rtl ? idx === 0 && offset < 0 : idx === 0 && offset > 0;
      const atLastRubber = rtl
        ? idx === LEITNER_TAB_ORDER.length - 1 && offset > 0
        : idx === LEITNER_TAB_ORDER.length - 1 && offset < 0;
      if (atFirstRubber || atLastRubber) {
        offset = offset / 3;
      }
      offset = Math.max(-width, Math.min(width, offset));
      tabDragOffsetPxRef.current = offset;
      setTabDragOffsetPx(offset);
      e.preventDefault();
    };

    const finishDrag = () => {
      const offset = tabDragOffsetPxRef.current;
      const width = el.clientWidth || 1;
      const elapsed = Math.max(1, Date.now() - startTime);
      const velocity = (lastX - startX) / elapsed;
      const idx = LEITNER_TAB_ORDER.indexOf(tabRef.current);
      const rtl = isRtlRef.current;
      let nextIdx = idx;
      if (rtl) {
        if ((offset > SWIPE_DISTANCE_PX || velocity > SWIPE_VELOCITY_PX_PER_MS) && idx < LEITNER_TAB_ORDER.length - 1) {
          nextIdx = idx + 1;
        } else if ((offset < -SWIPE_DISTANCE_PX || velocity < -SWIPE_VELOCITY_PX_PER_MS) && idx > 0) {
          nextIdx = idx - 1;
        } else if (Math.abs(offset) > width / 2) {
          nextIdx = offset > 0
            ? Math.min(LEITNER_TAB_ORDER.length - 1, idx + 1)
            : Math.max(0, idx - 1);
        }
      } else if ((offset < -SWIPE_DISTANCE_PX || velocity < -SWIPE_VELOCITY_PX_PER_MS) && idx < LEITNER_TAB_ORDER.length - 1) {
        nextIdx = idx + 1;
      } else if ((offset > SWIPE_DISTANCE_PX || velocity > SWIPE_VELOCITY_PX_PER_MS) && idx > 0) {
        nextIdx = idx - 1;
      } else if (Math.abs(offset) > width / 2) {
        nextIdx = offset < 0
          ? Math.min(LEITNER_TAB_ORDER.length - 1, idx + 1)
          : Math.max(0, idx - 1);
      }
      tabDragOffsetPxRef.current = 0;
      setTabDragOffsetPx(0);
      setTabDragging(false);
      const nextTab = LEITNER_TAB_ORDER[nextIdx];
      if (nextTab && nextTab !== tabRef.current) {
        tabRef.current = nextTab;
        setTab(nextTab);
      }
    };

    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;
      finishDrag();
      const swallowClick = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      el.addEventListener('click', swallowClick, { capture: true, once: true });
    };

    const onTouchCancel = () => {
      if (!dragging) return;
      dragging = false;
      tabDragOffsetPxRef.current = 0;
      setTabDragOffsetPx(0);
      setTabDragging(false);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [shouldRender]);

  const dismissRemoveConfirm = useCallback((cardId: number) => {
    if (removeClosingId === cardId) return;
    setRemoveClosingId(cardId);
    window.setTimeout(() => {
      setRemovePendingId((current) => (current === cardId ? null : current));
      setRemoveClosingId((current) => (current === cardId ? null : current));
    }, confirmAnimMs);
  }, [removeClosingId, confirmAnimMs]);

  useEffect(() => {
    setRemovePendingId(null);
    setRemoveClosingId(null);
    setExpandedWordId(null);
    setOpenBoxGuide(null);
  }, [tab]);

  useEffect(() => {
    const pendingId = removePendingId;
    if (pendingId == null || tab !== 'words') return;
    const handlePointerDown = (event: PointerEvent) => {
      const root = document.querySelector(`[data-leitner-confirm-root="${pendingId}"]`);
      if (root?.contains(event.target as Node)) return;
      dismissRemoveConfirm(pendingId);
    };
    const timeoutId = window.setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown, true);
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [removePendingId, tab, dismissRemoveConfirm]);

  const backdropVisible = useModalBackdropFade(shouldRender, !isOpen);

  if (!shouldRender) return null;

  const current = due[dueIndex];

  const review = async (knew: boolean) => {
    if (!current) return;
    await leitnerService.review(current.id, knew);
    const next = due.filter((_, i) => i !== dueIndex);
    setDue(next);
    setDueIndex((i) => (i >= next.length ? 0 : i));
    setRevealed(false);
    void loadSummary();
  };

  const saveForm = async () => {
    setError(null);
    try {
      if (editingId) {
        await leitnerService.update(editingId, form);
        emitLeitnerChanged();
      } else {
        await leitnerService.create(form);
        showSuccess(t('addedToBox', { count: 1 }));
        emitLeitnerChanged({ added: true });
      }
      setForm(emptyForm());
      setEditingId(null);
      setTab('words');
      await loadWords(1, query);
      await loadSummary();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('saveFailed');
      setError(msg);
      showError(msg);
    }
  };

  const farsiField = (
    value: string,
    onChange: (v: string) => void,
    extraClass = '',
    opts?: { multiline?: boolean; maxLength?: number }
  ) => (
    <FarsiKeyboardInput
      className={`mt-1 w-full rounded border border-white/70 bg-white/90 px-2 py-1 text-xs dark:border-white/10 dark:bg-slate-900/70 ${extraClass}`}
      value={value}
      onChange={onChange}
      keyboardPosition="fixed-above-anchor"
      keyboardTriggerPlacement="outside-right"
      multiline={opts?.multiline}
      rows={opts?.multiline ? 3 : undefined}
      maxLength={opts?.maxLength}
    />
  );

  const addFieldRow = (
    tone: string,
    icon: React.ReactNode,
    label: string,
    control: React.ReactNode
  ) => (
    <div className={`rounded-lg px-2.5 py-1.5 ${tone}`}>
      <div className="mb-0.5 flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-200">
        {icon}
        {label}
      </div>
      {control}
    </div>
  );

  const formatReviewDate = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(locale === 'fa' ? 'fa-IR' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const { panelBottom, panelRight, panelLeft } = learnerSidePanelPlacement(anchorStyle, isRtl);

  return (
    <div className={`fixed inset-0 z-[62] flex items-end ${isRtl ? 'justify-start' : 'justify-end'} ${isAnimatingOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
      <div
        className={`learner-side-backdrop fixed inset-0 pointer-events-auto${backdropVisible ? ' is-visible' : ''}`}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`fixed flex h-[52vh] max-h-[52vh] w-[70vw] max-w-[70vw] flex-col pointer-events-auto sm:h-[46vh] sm:max-h-[440px] sm:w-[60vw] sm:max-w-md ${learnerSidePanelMotionClass(isAnimatingOpen, isOpen, isRtl)}`}
        style={{
          bottom: panelBottom,
          ...(panelRight != null ? { right: panelRight } : {}),
          ...(panelLeft != null ? { left: panelLeft } : {}),
          ...(isOpen && !isAnimatingOpen
            ? { transform: isRtl ? 'translateX(-100%)' : 'translateX(100%)', opacity: 0.6 }
            : {})
        }}
      >
        <div
          data-tour-id="leitner-panel"
          className="learner-leitner-panel flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-rose-200 bg-white shadow-2xl dark:border-rose-900 dark:bg-slate-900"
          style={{ fontFamily: LEARNER_UI_PERSIAN_STACK }}
        >
        <div
          data-tour-id="leitner-header"
          className={`flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5 sm:px-4 sm:py-3 ${LEARNER_SETTINGS_MODAL_CHROME_CLASS}`}
        >
          <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
            <SquareStack className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-300" />
            <span className="truncate">{t('title')}</span>
            {summary && summary.dueCount > 0 ? (
              <span className="rounded-full bg-rose-600 px-2 text-xs font-semibold text-white">{summary.dueCount}</span>
            ) : null}
          </h2>
          <div className="flex shrink-0 flex-col items-center">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-slate-600 transition-colors hover:bg-slate-200/80 dark:text-slate-300 dark:hover:bg-gray-700"
              aria-label={t('close')}
              title={t('close')}
            >
              <X className="h-4 w-4" />
            </button>
            <ModalTourHelpButton tourPageKey="leitner" />
          </div>
        </div>
        {simpleHome && leitnerMenu ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <MmePickList
              items={LEITNER_TAB_ORDER.map((id) => ({
                id,
                title: t(`tab.${id}`),
                tourId: `leitner-tab-${id}`,
                ...(id === 'due' && (summary?.dueCount ?? 0) > 0 ? { badge: summary?.dueCount ?? 0 } : {}),
              }))}
              onPick={(id) => {
                setLeitnerMenu(false);
                setTab(id as Tab);
              }}
            />
          </div>
        ) : null}
        {simpleHome && !leitnerMenu ? (
          <div className="px-2 pt-1">
            <MmePickBack onBack={() => setLeitnerMenu(true)} />
          </div>
        ) : null}
        {!simpleHome ? (
        <LeitnerModalTabs
          active={tab}
          onChange={setTab}
          dueCount={summary?.dueCount ?? 0}
          dragOffsetPx={tabDragOffsetPx}
          isDragging={tabDragging}
          carouselRef={cardsScrollRef}
        />
        ) : null}
        <div className={`relative min-h-0 flex-1 overflow-hidden ${simpleHome && leitnerMenu ? 'hidden' : ''}`} hidden={simpleHome && leitnerMenu ? true : undefined}>
          <div dir="ltr" className="absolute inset-0 overflow-hidden">
            <div
              ref={cardsScrollRef}
              className="flex h-full items-start"
              style={{
                touchAction: tabDragging ? 'none' : 'pan-y',
                transform: `translate3d(calc(-${leitnerTabCarouselIndex(tab, isRtl) * 100}% + ${tabDragOffsetPx}px), 0, 0)`,
                transition: tabDragging || !tabMotionReady ? 'none' : 'transform 0.25s ease-out'
              }}
            >
              {leitnerTabCarouselOrder(isRtl).map((id) => (
                <div
                  key={id}
                  role="tabpanel"
                  className="h-full min-w-0 flex-[0_0_100%] shrink-0 self-stretch overflow-y-auto overscroll-y-contain"
                >
                  <div className="p-4" dir={direction}>
              {id === 'due' && (
                current ? (
                  <div data-tour-id="leitner-due-card" className="space-y-4">
                    <div dir="rtl" className="text-center text-3xl font-semibold text-gray-900 dark:text-gray-50">{current.persian}</div>
                    {current.phonetic ? (
                      <div dir={isRtl ? 'rtl' : 'ltr'} className="text-center text-sm text-gray-500">
                        {current.phonetic}
                      </div>
                    ) : null}
                    {current.audioUrl ? (
                      <div className="flex justify-center">
                        <VocabPronounceButton url={current.audioUrl} ariaLabel={current.persian} size="lg" />
                      </div>
                    ) : null}
                    {revealed ? (
                      <div className="rounded-xl bg-rose-50 p-3 text-center dark:bg-rose-950/40">
                        <div dir="ltr">{current.english}</div>
                        {current.imageUrl || current.example ? (
                          <div className="mt-2">
                            <VocabCollapseMedia
                              imageUrl={current.imageUrl}
                              example={current.example}
                              imagePlacement="stack"
                              exampleTextClassName="text-sm"
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <button type="button" className="w-full rounded-xl border border-rose-200 py-3" onClick={() => setRevealed(true)}>
                        {t('reveal')}
                      </button>
                    )}
                    {revealed ? (
                      <div className="grid grid-cols-2 gap-1.5">
                        <button
                          type="button"
                          onClick={() => void review(false)}
                          className="inline-flex items-center justify-center rounded-lg bg-amber-100 px-1.5 py-1 text-[11px] leading-tight text-amber-900"
                        >
                          <RotateCcw className="me-1 h-3 w-3 shrink-0" /> {t('didntKnow')}
                        </button>
                        <button
                          type="button"
                          onClick={() => void review(true)}
                          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-1.5 py-1 text-[11px] leading-tight text-white"
                        >
                          <Check className="me-1 h-3 w-3 shrink-0" /> {t('knew')}
                        </button>
                      </div>
                    ) : null}
                    <div className="text-center text-xs text-gray-500">{t('dueProgress', { current: dueIndex + 1, total: due.length })}</div>
                  </div>
                ) : (
                  <p data-tour-id="leitner-due-card" className="py-8 text-center text-sm text-gray-500">{t('noDue')}</p>
                )
              )}

              {id === 'words' && (
                <div data-tour-id="leitner-word-list" className="space-y-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute top-2.5 left-2 h-4 w-4 text-gray-400" />
                    <FarsiKeyboardInput
                      value={query}
                      onChange={(v) => {
                        setQuery(v);
                        void loadWords(1, v);
                      }}
                      keyboardPosition="fixed-above-anchor"
                      keyboardTriggerPlacement="outside-right"
                      className="w-full rounded-lg border border-rose-200 py-2 ps-8 pe-2 text-sm"
                      placeholder={t('searchPlaceholder')}
                    />
                  </div>
                  {wordsLoading && words.length === 0 ? (
                    <div className="flex justify-center py-6 text-rose-600">
                      <Loader2 className="h-5 w-5 animate-spin" aria-label={t('loading')} />
                    </div>
                  ) : null}
                  {words.map((card) => {
                    const isPending = removePendingId === card.id;
                    const isClosing = removeClosingId === card.id;
                    const isConfirmOpen = isPending || isClosing;
                    const isRemoving = removingId === card.id;
                    const isExpanded = expandedWordId === card.id;
                    const pos = WORD_POS_I18N[card.partOfSpeech ?? 5];
                    return (
                    <div
                      key={card.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setExpandedWordId(isExpanded ? null : card.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedWordId(isExpanded ? null : card.id);
                        }
                      }}
                      className={`cursor-pointer rounded-lg border px-2 py-1 text-start dark:border-rose-900 ${
                        highlightId === card.id
                          ? 'border-rose-400 bg-rose-50 ring-2 ring-rose-300 dark:bg-rose-950/40'
                          : isExpanded
                            ? 'border-rose-200 bg-rose-50/50 dark:bg-rose-950/20'
                            : 'border-rose-100'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div dir="rtl" className="text-sm font-semibold">{card.persian}</div>
                          <div dir="ltr" className="text-xs text-gray-500">{card.english}</div>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          <span className="rounded-full bg-rose-100 px-1.5 py-px text-[10px] leading-4 text-rose-800 dark:bg-rose-950/60 dark:text-rose-200">
                            {t('box')} {card.boxNumber}
                          </span>
                          <button
                            type="button"
                            className="rounded-full bg-rose-100 px-1.5 py-px text-[10px] leading-4 text-rose-800 hover:bg-rose-200 dark:bg-rose-950/60 dark:text-rose-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              setForm(cardToWrite(card));
                              setEditingId(card.id);
                              setTab('add');
                            }}
                          >
                            {t('edit')}
                          </button>
                          <div className="relative flex flex-shrink-0 items-center self-stretch">
                            {isConfirmOpen && (
                              <div
                                data-leitner-confirm-root={card.id}
                                className={`absolute right-full top-1/2 z-10 mr-1.5 flex min-w-[10.75rem] -translate-y-1/2 items-center gap-2 rounded-lg border border-gray-400/35 bg-white/20 px-3 py-0.5 shadow-lg backdrop-blur-[2px] dark:border-gray-500/35 dark:bg-gray-950/20 ${
                                  isClosing ? 'animate-favorite-confirm-out' : 'animate-favorite-confirm-in'
                                }`}
                                onClick={(e) => e.stopPropagation()}
                                role="group"
                                aria-label={t('confirmRemove')}
                              >
                                <span className="whitespace-nowrap text-xs font-semibold text-gray-800 drop-shadow-sm dark:text-gray-100">
                                  {t('confirmRemove')}
                                </span>
                                <button
                                  type="button"
                                  disabled={isRemoving || isClosing}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dismissRemoveConfirm(card.id);
                                  }}
                                  className="min-w-[2.25rem] rounded-md px-2.5 py-1 text-xs font-medium text-gray-800 hover:bg-black/10 disabled:opacity-50 dark:text-gray-100 dark:hover:bg-white/10"
                                >
                                  {t('confirmRemoveNo')}
                                </button>
                                <button
                                  type="button"
                                  disabled={isRemoving || isClosing}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setRemovingId(card.id);
                                    try {
                                      await leitnerService.remove(card.id);
                                      setRemovePendingId(null);
                                      setRemoveClosingId(null);
                                      setWords((prev) => prev.filter((row) => row.id !== card.id));
                                      setTotal((count) => Math.max(0, count - 1));
                                      await loadSummary();
                                      emitLeitnerChanged();
                                      showSuccess(t('removedFromBox'));
                                    } catch (err) {
                                      showError(err instanceof Error ? err.message : t('deleteFailed'));
                                    } finally {
                                      setRemovingId(null);
                                    }
                                  }}
                                  className="min-w-[2.25rem] rounded-md bg-rose-500/25 px-2.5 py-1 text-xs font-medium text-rose-800 hover:bg-rose-500/35 disabled:opacity-50 dark:text-rose-200 dark:hover:bg-rose-500/30"
                                >
                                  {isRemoving ? (
                                    <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
                                  ) : (
                                    t('confirmRemoveYes')
                                  )}
                                </button>
                              </div>
                            )}
                            <button
                              type="button"
                              disabled={isRemoving || isConfirmOpen}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isConfirmOpen) return;
                                setRemoveClosingId(null);
                                setRemovePendingId(card.id);
                              }}
                              className="relative z-20 shrink-0 rounded-lg p-1.5 text-rose-600 transition-colors hover:bg-rose-100 disabled:opacity-100 dark:text-rose-300 dark:hover:bg-rose-950/50"
                              title={t('delete')}
                              aria-label={t('delete')}
                            >
                              {isRemoving && !isConfirmOpen ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-rose-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} aria-hidden />
                        </div>
                      </div>
                      <CollapsibleFadePanel expanded={isExpanded}>
                          <div
                            dir={isRtl ? 'rtl' : 'ltr'}
                            className="mt-2 flex items-start gap-2 border-t border-rose-100 pt-2 text-xs text-gray-600 dark:border-rose-900 dark:text-gray-300"
                          >
                            <div className="min-w-0 flex-1 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-rose-100 px-1.5 py-px text-[10px] leading-4 text-rose-800 dark:bg-rose-950/60 dark:text-rose-200">
                                {locale === 'fa' ? pos.fa : pos.en}
                              </span>
                              {card.audioUrl ? (
                                <VocabPronounceButton url={card.audioUrl} ariaLabel={card.persian} size="sm" />
                              ) : null}
                            </div>
                            {card.phonetic ? (
                              <div dir={isRtl ? 'rtl' : 'ltr'} className="flex items-center gap-1.5">
                                <Type className="h-3 w-3 shrink-0 text-sky-500" aria-hidden />
                                {card.phonetic}
                              </div>
                            ) : null}
                            {card.example ? (
                              <div className="flex gap-1.5">
                                <Quote className="mt-0.5 h-3 w-3 shrink-0 text-rose-300" aria-hidden />
                                <p dir={isRtl ? 'rtl' : 'ltr'} className="min-w-0 whitespace-pre-wrap">
                                  {card.example}
                                </p>
                              </div>
                            ) : null}
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500">
                              <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] leading-tight text-rose-800 ring-1 ring-rose-100 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-900">
                                <CalendarDays className="h-2.5 w-2.5 shrink-0" aria-hidden />
                                {t('nextReview')}: {formatReviewDate(card.nextReviewAt)}
                              </span>
                              <span className="text-[11px]">{t('reviewCount', { count: card.reviewCount ?? 0 })}</span>
                            </div>
                            </div>
                            {card.imageUrl ? <VocabWordImage url={card.imageUrl} /> : null}
                          </div>
                      </CollapsibleFadePanel>
                    </div>
                    );
                  })}
                  {total > words.length ? (
                    <div className="flex justify-center pt-2">
                      <button
                        type="button"
                        onClick={() => void loadWords(page + 1, query, true)}
                        disabled={wordsLoadingMore}
                        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-3 text-sm font-medium text-white shadow-lg transition-all duration-200 hover:from-purple-700 hover:to-pink-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-lg dark:from-purple-500 dark:to-pink-500 dark:hover:from-purple-600 dark:hover:to-pink-600"
                      >
                        {wordsLoadingMore ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            <span>{t('loading')}</span>
                          </>
                        ) : (
                          <>
                            <span>{t('loadMore')}</span>
                            <ChevronDown className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

              {id === 'add' && (
                <div data-tour-id="leitner-add-form" className="space-y-2">
                  {addFieldRow(
                    ADD_FIELD_TONES[0],
                    <Languages className="h-3.5 w-3.5 text-rose-500" aria-hidden />,
                    t('field.persian'),
                    farsiField(form.persian, (v) => setForm({ ...form, persian: v }))
                  )}
                  {addFieldRow(
                    ADD_FIELD_TONES[1],
                    <Languages className="h-3.5 w-3.5 text-sky-500" aria-hidden />,
                    t('field.english'),
                    <input
                      className="mt-1 w-full rounded border border-white/70 bg-white/90 px-2 py-1 text-xs dark:border-white/10 dark:bg-slate-900/70"
                      dir="ltr"
                      value={form.english}
                      onChange={(e) => setForm({ ...form, english: e.target.value })}
                    />
                  )}
                  {addFieldRow(
                    ADD_FIELD_TONES[2],
                    <Tags className="h-3.5 w-3.5 text-violet-500" aria-hidden />,
                    t('field.pos'),
                    <div className="mt-1">
                      <ProfileComboList
                        id="leitner-word-type"
                        value={String(form.partOfSpeech ?? WordPartOfSpeech.Other)}
                        onChange={(v) => setForm({ ...form, partOfSpeech: Number(v) as WordPartOfSpeech })}
                        options={WORD_POS_OPTIONS.map((o) => ({
                          value: String(o.value),
                          label: locale === 'fa' ? WORD_POS_I18N[o.value].fa : o.label
                        }))}
                        placeholder={t('field.pos')}
                        aria-label={t('field.pos')}
                        size="sm"
                        useFixedPortal
                        triggerClassName="rounded border-white/70 bg-white/90 shadow-none dark:border-white/10 dark:bg-slate-900/70"
                        accentSelectedClass="bg-rose-50 dark:bg-rose-950/45 text-rose-900 dark:text-rose-100"
                        accentCheckClass="text-rose-600 dark:text-rose-400"
                      />
                    </div>
                  )}
                  {addFieldRow(
                    ADD_FIELD_TONES[3],
                    <Type className="h-3.5 w-3.5 text-amber-600" aria-hidden />,
                    t('field.phonetic'),
                    <input
                      className="mt-1 w-full rounded border border-white/70 bg-white/90 px-2 py-1 text-xs dark:border-white/10 dark:bg-slate-900/70"
                      dir={isRtl ? 'rtl' : 'ltr'}
                      value={form.phonetic ?? ''}
                      onChange={(e) => setForm({ ...form, phonetic: e.target.value })}
                    />
                  )}
                  {addFieldRow(
                    ADD_FIELD_TONES[4],
                    <Quote className="h-3.5 w-3.5 text-emerald-600" aria-hidden />,
                    t('field.example'),
                    <>
                      {farsiField(form.example ?? '', (v) => setForm({ ...form, example: v.slice(0, 500) }), '', {
                        multiline: true,
                        maxLength: 500
                      })}
                      <div className="mt-0.5 text-end text-[10px] text-gray-400">
                        {(form.example ?? '').length}/500
                      </div>
                    </>
                  )}
                  {error ? <p className="text-xs text-red-600">{error}</p> : null}
                  <button type="button" onClick={() => void saveForm()} className="inline-flex w-full items-center justify-center gap-1 rounded-xl bg-rose-600 py-2 text-white">
                    <Plus className="h-4 w-4" /> {editingId ? t('save') : t('addWord')}
                  </button>
                </div>
              )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {summary ? (
          <div data-tour-id="leitner-boxes" className="relative shrink-0 border-t border-rose-100 dark:border-rose-900">
            {openBoxGuide != null ? (
              <div className="absolute inset-x-2 bottom-full z-30 mb-1 rounded-xl border border-rose-200 bg-white p-2.5 pe-7 text-start shadow-lg dark:border-rose-800 dark:bg-slate-900">
                <button
                  type="button"
                  className="absolute top-1.5 end-1.5 rounded p-0.5 text-gray-400 hover:bg-rose-50 hover:text-gray-700"
                  onClick={() => setOpenBoxGuide(null)}
                  aria-label={t('close')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <div className="space-y-1 text-[10px] leading-snug text-gray-600 dark:text-gray-300">
                  <p>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{t('boxGuide.intervalLabel')}: </span>
                    {BOX_INTERVAL_DAYS[openBoxGuide - 1] === 1
                      ? t('boxGuide.intervalOne')
                      : t('boxGuide.interval', { count: BOX_INTERVAL_DAYS[openBoxGuide - 1] })}
                  </p>
                  <p>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{t('boxGuide.howLabel')}: </span>
                    {t(`boxGuide.${openBoxGuide}.how`)}
                  </p>
                  <p className="flex gap-1">
                    <Info className="mt-0.5 h-3 w-3 shrink-0 text-rose-400" aria-hidden />
                    <span>{t(`boxGuide.${openBoxGuide}.tip`)}</span>
                  </p>
                  <p className="text-gray-500 dark:text-gray-400">{t('boxGuide.reset')}</p>
                </div>
              </div>
            ) : null}
            <div className="flex justify-around px-1 py-1.5">
              {(summary.boxCounts ?? []).map((count, i) => {
                const box = i + 1;
                const isOn = openBoxGuide === box;
                return (
                  <button
                    key={box}
                    type="button"
                    onClick={() => setOpenBoxGuide(isOn ? null : box)}
                    className={`rounded-md px-1.5 py-1 text-[9px] font-medium transition sm:text-[11px] ${
                      BOX_TONES[Math.min(i, BOX_TONES.length - 1)]
                    } ${isOn ? 'ring-2 ring-slate-800 ring-offset-1 dark:ring-white dark:ring-offset-slate-900' : ''}`}
                  >
                    {t('box')} {box}: {count}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        </div>
      </div>
    </div>
  );
};
